import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import {
  Server, Database, Users, Building2, Layers, Code, Globe, Brain,
  BarChart3, Shield, Cable, Loader2, FileText, DollarSign, Bell, Tag,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function MetricCard({ value, label, icon: Icon, color = '#6366f1', sub }) {
  return (
    <div className="border rounded-xl p-4 bg-card">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 shrink-0" style={{ color }} />
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>{typeof value === 'number' ? value.toLocaleString() : value}</div>
      {sub && <span className="text-[10px] text-muted-foreground">{sub}</span>}
    </div>
  );
}

function ProgressBar({ value, max, color = '#6366f1', label }) {
  const pct = max > 0 ? Math.round(value / max * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-xs">
      <span className="w-24 text-muted-foreground truncate">{label}</span>
      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="font-bold tabular-nums w-10 text-right" style={{ color }}>{pct}%</span>
    </div>
  );
}

export default function PlatformMetrics({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/platform-metrics`, { headers })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data) return <div className="p-6 text-center text-muted-foreground">Failed to load metrics</div>;

  const e = data.entities || {};
  const c = data.codebase || {};
  const db = data.database || {};
  const ub = data.users_breakdown || {};
  const dc = data.data_completeness || {};
  const dep = data.deployment || {};
  const feat = data.features || {};
  const tb = data.tenants_breakdown || {};

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-6xl mx-auto space-y-8" data-testid="platform-metrics">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Platform Metrics</h2>
          <p className="text-xs text-muted-foreground mt-1">Real-time statistics for the entire LodgiFlow platform.</p>
        </div>

        {/* Core Entity Metrics */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Core Entities</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <MetricCard value={e.tenants} label="Tenants" icon={Building2} color="#7c3aed" />
            <MetricCard value={e.properties} label="Properties" icon={Globe} color="#0891b2" />
            <MetricCard value={e.users} label="Users" icon={Users} color="#2563eb" />
            <MetricCard value={e.products} label="Products" icon={Layers} color="#059669" sub="AI-enriched" />
            <MetricCard value={e.vendors} label="Vendors" icon={Tag} color="#d97706" />
            <MetricCard value={e.platforms} label="Platform Types" icon={Server} color="#6366f1" />
          </div>
        </div>

        {/* Deployment Metrics */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Deployment & Assignments</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            <MetricCard value={e.assignments} label="Assignments" icon={Cable} color="#6366f1" />
            <MetricCard value={dep.avg_platforms_per_property} label="Avg/Property" icon={BarChart3} color="#0891b2" />
            <MetricCard value={dep.unique_products_deployed} label="Products Deployed" icon={Layers} color="#059669" />
            <MetricCard value={e.integrations} label="Integration Blueprints" icon={Cable} color="#7c3aed" />
            <MetricCard value={e.property_groups} label="Property Groups" icon={Globe} color="#d97706" />
          </div>
        </div>

        {/* User Breakdown */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">User Breakdown</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            <MetricCard value={ub.super_admins} label="Super Admins" icon={Shield} color="#7c3aed" />
            <MetricCard value={ub.tenant_admins} label="Tenant Admins" icon={Users} color="#2563eb" />
            <MetricCard value={ub.managers} label="Managers" icon={Users} color="#059669" />
            <MetricCard value={ub.editors} label="Editors" icon={Users} color="#d97706" />
            <MetricCard value={ub.viewers} label="Viewers" icon={Users} color="#6b7280" />
          </div>
        </div>

        {/* Tenant Breakdown */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Tenant Breakdown</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            <MetricCard value={tb.trial} label="Trial Tenants" icon={Building2} color="#d97706" />
            <MetricCard value={tb.active} label="Paid Tenants" icon={Building2} color="#059669" />
            <MetricCard value={e.tenants} label="Total Tenants" icon={Building2} color="#6366f1" />
          </div>
          {tb.properties_by_tenant && Object.keys(tb.properties_by_tenant).length > 0 && (
            <div className="border rounded-xl p-4 bg-card">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-2">Properties by Tenant</span>
              <div className="space-y-1.5">
                {Object.entries(tb.properties_by_tenant).sort((a, b) => b[1] - a[1]).map(([tid, count]) => (
                  <ProgressBar key={tid} label={tid || 'unassigned'} value={count} max={e.properties} color="#6366f1" />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Data Completeness */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Data Completeness</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="border rounded-xl p-4 bg-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold">Products</span>
                <span className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk', color: dc.product_completeness_pct >= 80 ? '#059669' : '#d97706' }}>{dc.product_completeness_pct}%</span>
              </div>
              <div className="space-y-1">
                <ProgressBar label="Descriptions" value={dc.products_with_description} max={e.products} color="#059669" />
                <ProgressBar label="Websites" value={dc.products_with_website} max={e.products} color="#2563eb" />
              </div>
            </div>
            <div className="border rounded-xl p-4 bg-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold">Vendors</span>
                <span className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk', color: dc.vendor_completeness_pct >= 80 ? '#059669' : '#d97706' }}>{dc.vendor_completeness_pct}%</span>
              </div>
              <ProgressBar label="Websites" value={dc.vendors_with_website} max={e.vendors} color="#2563eb" />
            </div>
            <div className="border rounded-xl p-4 bg-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold">Platforms</span>
                <span className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk', color: dc.platform_completeness_pct >= 80 ? '#059669' : '#d97706' }}>{dc.platform_completeness_pct}%</span>
              </div>
              <ProgressBar label="Descriptions" value={dc.platforms_with_description} max={e.platforms} color="#059669" />
            </div>
          </div>
        </div>

        {/* Financial & Operations */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Financial & Operations</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            <MetricCard value={e.contracts} label="Contracts" icon={FileText} color="#6366f1" />
            <MetricCard value={e.invoices} label="Invoices" icon={DollarSign} color="#059669" />
            <MetricCard value={e.spend_records} label="Spend Records" icon={DollarSign} color="#2563eb" />
            <MetricCard value={e.incidents} label="Incidents" icon={Bell} color="#dc2626" />
            <MetricCard value={e.notifications} label="Notifications" icon={Bell} color="#d97706" />
            <MetricCard value={e.audit_entries} label="Audit Entries" icon={FileText} color="#6b7280" />
          </div>
        </div>

        {/* Codebase Stats */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Codebase</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            <MetricCard value={(c.total_lines || 0).toLocaleString()} label="Lines of Code" icon={Code} color="#6366f1" />
            <MetricCard value={c.total_characters ? `${(c.total_characters / 1000000).toFixed(1)}M` : '0'} label="Characters" icon={Code} color="#7c3aed" />
            <MetricCard value={c.api_endpoints} label="API Endpoints" icon={Server} color="#0891b2" />
            <MetricCard value={c.backend_modules} label="Backend Modules" icon={Database} color="#059669" />
            <MetricCard value={c.total_components} label="Components" icon={Layers} color="#2563eb" sub={`${c.app_components} app + ${c.ui_components} UI`} />
            <MetricCard value={c.backend_files} label="Python Files" icon={Code} color="#d97706" />
            <MetricCard value={c.frontend_files} label="JS/JSX Files" icon={Code} color="#dc2626" />
            <MetricCard value={db.collections} label="DB Collections" icon={Database} color="#6b7280" sub={`${db.total_documents?.toLocaleString()} docs`} />
          </div>
        </div>

        {/* Feature Counts */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Feature Counts</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            <MetricCard value={feat.ai_assistants_customer + feat.ai_assistants_internal} label="AI Assistants" icon={Brain} color="#7c3aed" sub={`${feat.ai_assistants_customer} customer + ${feat.ai_assistants_internal} internal`} />
            <MetricCard value={feat.report_endpoints} label="Reports" icon={BarChart3} color="#2563eb" />
            <MetricCard value={feat.visualization_layouts} label="Map Layouts" icon={Globe} color="#0891b2" sub={`+ ${feat.card_view_modes} card modes`} />
            <MetricCard value={feat.erp_connectors} label="ERP Connectors" icon={Cable} color="#059669" />
            <MetricCard value={feat.rbac_permissions} label="Permissions" icon={Shield} color="#dc2626" />
            <MetricCard value={feat.languages} label="Languages" icon={Globe} color="#d97706" />
            <MetricCard value={feat.mobile_exec_views} label="Mobile Views" icon={Layers} color="#6366f1" />
          </div>
        </div>

        {/* Database Collections Detail */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Database Collections</h3>
          <div className="border rounded-xl overflow-hidden">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-0 divide-x divide-y">
              {Object.entries(db.collection_stats || {}).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
                <div key={name} className="p-3 text-xs">
                  <span className="text-muted-foreground block truncate">{name}</span>
                  <span className="font-bold tabular-nums text-sm">{count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}
